import { IdGenerator } from "utils/idGenerator"
import { BaseLinkTypes, LinkProperties, LinkTargetProperties } from "global/components/linkSettings/interfaces"
import { getNewlinkTargetProperties } from "../../widgets/widgetsUtils"

export type TimelineDisplayOrder = "first_to_last" | "last_to_first"
export type TimelineDisplayMode = "full" | "progressive"
export type TimelineItemMediaType = "none" | "image" | "video" | "gallery"
export type TimelineItemMediaPosition = "above" | "below"
export type TimelineItemMediaProportion = "16:9" | "9:16"
export type TimelineStoredMediaType = "image" | "video" | "gallery"

export interface TimelineItemData {
    id: string
    roof: string
    title: string
    mediaType: TimelineItemMediaType
    mediaPosition: TimelineItemMediaPosition
    mediaProportion: TimelineItemMediaProportion
    mediaIds: string[]
    /** Keeps each media type's selection when switching tabs. */
    mediaIdsByType: Partial<Record<TimelineStoredMediaType, string[]>>
    text: string
    ctaEnabled: boolean
    ctaLabel: string
    ctaLinkProperties: LinkProperties<BaseLinkTypes>
    ctaLinkTargetProperties: LinkTargetProperties
}

export const TIMELINE_MEDIA_ASPECT_RATIOS: Record<TimelineItemMediaProportion, { width: number, height: number }> = {
    "16:9": { width: 220, height: 124 },
    "9:16": { width: 124, height: 220 },
}

/** Matches gallery popup list ratio (~16:9). */
export const TIMELINE_GALLERY_THUMB_SIZE = { width: 160, height: 90 }

export const TIMELINE_MEDIA_PROPORTION_OPTIONS = [
    { value: "16:9", label: "16:9 (אופקי)" },
    { value: "9:16", label: "9:16 (אנכי)" },
]

/**
 * Inserted into item text to mark where the front should render a "read more" control.
 * Front should split on this token (text before = preview, after = collapsed).
 */
export const TIMELINE_READ_MORE_MARKER = "⟦READ_MORE⟧"

export interface TimelineSegmentData {
    id: string
    title: string
    items: TimelineItemData[]
}

export interface TimelineData {
    mediaLabel: string
    displayOrder: TimelineDisplayOrder
    displayMode: TimelineDisplayMode
    showSegmentTitles: boolean
    limitSegments: boolean
    maxSegments: number
    segments: TimelineSegmentData[]
}

export interface TimelineArticleSpecialData {
    timelineData: TimelineData
    imageIds: string[]
    resourceIds: string[]
}

export const DEFAULT_TIMELINE_CTA_LABEL = "לכתבה המלאה>"
export const DEFAULT_TIMELINE_MEDIA_LABEL = "עדכון חדש"
export const MIN_TIMELINE_GALLERY_IMAGES = 4

const createDefaultCtaLinkProperties = (): LinkProperties<BaseLinkTypes> => ({
    type: BaseLinkTypes.EXTERNAL_URL,
    link: { id: "", description: "", publishedLink: "" },
})

export const createEmptyTimelineItem = (): TimelineItemData => ({
    id: IdGenerator.generate(),
    roof: "",
    title: "",
    mediaType: "none",
    mediaPosition: "above",
    mediaProportion: "16:9",
    mediaIds: [],
    mediaIdsByType: {},
    text: "",
    ctaEnabled: false,
    ctaLabel: DEFAULT_TIMELINE_CTA_LABEL,
    ctaLinkProperties: createDefaultCtaLinkProperties(),
    ctaLinkTargetProperties: getNewlinkTargetProperties(),
})

const isStoredMediaType = (mediaType: TimelineItemMediaType): mediaType is TimelineStoredMediaType =>
    mediaType === "image" || mediaType === "video" || mediaType === "gallery"

export const getStoredMediaIds = (item: TimelineItemData, mediaType: TimelineStoredMediaType): string[] => {
    const stored = item.mediaIdsByType && item.mediaIdsByType[mediaType]
    return Array.isArray(stored) ? stored.filter(Boolean) : []
}

export const withStoredMediaIds = (
    item: TimelineItemData,
    mediaType: TimelineStoredMediaType,
    mediaIds: string[],
): Partial<Record<TimelineStoredMediaType, string[]>> => ({
    ...(item.mediaIdsByType || {}),
    [mediaType]: mediaIds.filter(Boolean),
})

const plainTextFromRawState = (raw: any): string => {
    if (!raw || !Array.isArray(raw.blocks)) {
        return ""
    }
    return raw.blocks.map((block: any) => (block && block.text) || "").join("\n")
}

const normalizeItemText = (item: any): string => {
    if (item && typeof item.text === "string") {
        return item.text
    }
    if (item && item.textRawState) {
        return plainTextFromRawState(item.textRawState)
    }
    return ""
}

const normalizeCtaLinkProperties = (item: any): LinkProperties<BaseLinkTypes> => {
    if (item && item.ctaLinkProperties && item.ctaLinkProperties.type) {
        return {
            type: item.ctaLinkProperties.type,
            link: {
                id: (item.ctaLinkProperties.link && item.ctaLinkProperties.link.id) || "",
                description: (item.ctaLinkProperties.link && item.ctaLinkProperties.link.description) || "",
                publishedLink: (item.ctaLinkProperties.link && item.ctaLinkProperties.link.publishedLink) || "",
                extraLinkData: item.ctaLinkProperties.link && item.ctaLinkProperties.link.extraLinkData,
            },
        }
    }

    if (item && item.ctaArticleId) {
        return {
            type: BaseLinkTypes.ARTICLE,
            link: { id: item.ctaArticleId, description: "", publishedLink: "" },
        }
    }

    if (item && item.ctaLink) {
        return {
            type: BaseLinkTypes.EXTERNAL_URL,
            link: { id: item.ctaLink, description: item.ctaLink, publishedLink: item.ctaLink },
        }
    }

    return createDefaultCtaLinkProperties()
}

export const createEmptyTimelineSegment = (title = ""): TimelineSegmentData => ({
    id: IdGenerator.generate(),
    title,
    items: [],
})

export const createDefaultTimelineData = (): TimelineData => ({
    mediaLabel: DEFAULT_TIMELINE_MEDIA_LABEL,
    displayOrder: "first_to_last",
    displayMode: "full",
    showSegmentTitles: true,
    limitSegments: false,
    maxSegments: 1,
    segments: [],
})

const normalizeTimelineItem = (item: any): TimelineItemData => {
    const empty = createEmptyTimelineItem()
    const ctaLinkProperties = normalizeCtaLinkProperties(item)
    const hasCtaLink = !!(ctaLinkProperties.link && ctaLinkProperties.link.id)
    const {
        ctaLink: _ctaLink,
        ctaArticleId: _ctaArticleId,
        textRawState: _textRawState,
        ...itemRest
    } = item || {}

    const mediaIds = Array.isArray(item && item.mediaIds) ? item.mediaIds.filter(Boolean) : []
    const mediaType: TimelineItemMediaType = (item && item.mediaType) || "none"
    const mediaIdsByType: Partial<Record<TimelineStoredMediaType, string[]>> = {
        ...((item && item.mediaIdsByType) || {}),
    }

    if (isStoredMediaType(mediaType) && mediaIds.length && !mediaIdsByType[mediaType]) {
        mediaIdsByType[mediaType] = mediaIds
    }

    return {
        ...empty,
        ...itemRest,
        mediaType,
        mediaIds,
        mediaIdsByType,
        text: normalizeItemText(item),
        mediaProportion: item && item.mediaProportion === "9:16" ? "9:16" : "16:9",
        ctaEnabled: !!(item && (item.ctaEnabled || hasCtaLink || item.ctaLink || item.ctaArticleId)),
        ctaLabel: (item && item.ctaLabel) || DEFAULT_TIMELINE_CTA_LABEL,
        ctaLinkProperties,
        ctaLinkTargetProperties: (item && item.ctaLinkTargetProperties) || getNewlinkTargetProperties(),
    }
}

const normalizeTimelineSegment = (segment: any): TimelineSegmentData => ({
    id: (segment && segment.id) || IdGenerator.generate(),
    title: (segment && segment.title) || "",
    items: Array.isArray(segment && segment.items) ? segment.items.map(normalizeTimelineItem) : [],
})

export const collectTimelineMediaResourceIds = (timelineData: TimelineData) => {
    const imageIds: string[] = []
    const resourceIds: string[] = []

    ;(timelineData && timelineData.segments || []).forEach(segment => {
        ;(segment.items || []).forEach(item => {
            const byType = item.mediaIdsByType || {}
            const activeIds = item.mediaIds || []

            if (item.mediaType === "video") {
                activeIds.forEach(id => id && resourceIds.push(id))
            } else if (item.mediaType === "image" || item.mediaType === "gallery") {
                activeIds.forEach(id => id && imageIds.push(id))
            }

            ;(byType.image || []).forEach(id => id && imageIds.push(id))
            ;(byType.gallery || []).forEach(id => id && imageIds.push(id))
            ;(byType.video || []).forEach(id => id && resourceIds.push(id))
        })
    })

    return { imageIds: _.uniq(imageIds), resourceIds: _.uniq(resourceIds) }
}

export const ensureTimelineSpecialData = (specialData: any): TimelineArticleSpecialData => {
    const defaults = createDefaultTimelineData()
    const raw = specialData && specialData.timelineData ? specialData.timelineData : {}
    const timelineData: TimelineData = {
        ...defaults,
        ...raw,
        segments: Array.isArray(raw.segments) ? raw.segments.map(normalizeTimelineSegment) : [],
    }
    const { imageIds, resourceIds } = collectTimelineMediaResourceIds(timelineData)

    return {
        timelineData,
        imageIds,
        resourceIds,
    }
}

export const moveArrayItem = <T>(items: T[], fromIndex: number, toIndex: number): T[] => {
    if (toIndex < 0 || toIndex >= items.length) {
        return items
    }
    const next = items.slice()
    const [item] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, item)
    return next
}

export const insertTimelineReadMoreMarker = (text: string, selectionStart: number, selectionEnd: number) => {
    const marker = TIMELINE_READ_MORE_MARKER
    const current = text || ""
    let adjustedStart = selectionStart
    let adjustedEnd = selectionEnd
    let cleaned = ""
    let i = 0

    while (i < current.length) {
        if (current.indexOf(marker, i) === i) {
            if (i < selectionStart) {
                adjustedStart -= marker.length
            }
            if (i < selectionEnd) {
                adjustedEnd -= marker.length
            }
            i += marker.length
            // Drop a wrapping newline that was inserted with a previous marker.
            if (current.charAt(i) === "\n") {
                if (i < selectionStart) {
                    adjustedStart -= 1
                }
                if (i < selectionEnd) {
                    adjustedEnd -= 1
                }
                i += 1
            }
        } else {
            cleaned += current.charAt(i)
            i += 1
        }
    }

    const start = Math.max(0, Math.min(adjustedStart, cleaned.length))
    const end = Math.max(start, Math.min(adjustedEnd, cleaned.length))
    const before = cleaned.slice(0, start)
    const after = cleaned.slice(end)
    const leadingNl = before.length > 0 && !before.endsWith("\n") ? "\n" : ""
    const trailingNl = after.length > 0 && !after.startsWith("\n") ? "\n" : ""
    const insertion = `${leadingNl}${marker}${trailingNl}`
    const nextText = before + insertion + after

    return {
        text: nextText,
        cursor: before.length + insertion.length,
    }
}
